import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  CATALOG_THUMB_PX,
  compressImageForUpload,
  toOptimizedThumbDeliveryUrl,
} from '../utils/compressImageUpload';
import { getApiBaseUrl, getStoredToken } from '../services/httpClient';

const CLOUDINARY_CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dml5dugde';
const CLOUDINARY_UPLOAD_PRESET =
  process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'locsy_business_profiles';
const THUMB_PX = CATALOG_THUMB_PX;

export function buildProductThumbPrompt({ name, details, itemKind }) {
  const label = itemKind === 'product' ? 'product' : 'service';
  return `Create ONE square marketplace thumbnail for a local Indian ${label} catalog card.

Name: ${String(name || '').trim() || '(unnamed)'}
Details: ${String(details || '').trim() || 'N/A'}

STRICT OUTPUT (thumbnail only — do not invent other sizes or variants):
- Exact canvas: ${THUMB_PX}×${THUMB_PX} pixels (not 1024, not 2K, not 4K)
- Format: PNG with transparent background (no studio floor, no grey backdrop, alpha = 0 outside subject)
- File size target: under 150 KB (web thumbnail). Prefer flat lighting and limited detail so PNG stays small.
- Framing: single centered subject, slight padding, crisp silhouette on transparency
- No text, logos, watermarks, price tags, utensils, napkins, or busy props
- Photo-realistic, high contrast, readable at ~80px on a phone list
- Output ONLY this one ${THUMB_PX}×${THUMB_PX} transparent PNG (do not attach a second opaque version).`;
}

async function uploadProductThumbnail(file) {
  if (!CLOUDINARY_CLOUD_NAME || !CLOUDINARY_UPLOAD_PRESET) {
    throw new Error('Cloudinary is not configured.');
  }
  const prepared = await compressImageForUpload(file);
  const formData = new FormData();
  formData.append('file', prepared);
  formData.append('upload_preset', CLOUDINARY_UPLOAD_PRESET);
  formData.append('folder', 'smartbill_product_thumbnails');
  const cleanAxios = axios.create();
  if (cleanAxios.defaults.headers.common) {
    delete cleanAxios.defaults.headers.common.Authorization;
    delete cleanAxios.defaults.headers.common.authorization;
  }
  const response = await cleanAxios.post(
    `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
    formData,
  );
  const secureUrl = response?.data?.secure_url || response?.data?.url || '';
  if (!secureUrl) throw new Error('Thumbnail upload failed');
  return toOptimizedThumbDeliveryUrl(secureUrl, THUMB_PX);
}

/**
 * Product / service image: (1) online stock search (2) AI prompt + upload.
 */
const ProductImagePicker = ({
  value = '',
  onChange,
  name = '',
  details = '',
  itemKind = 'product',
}) => {
  const fileRef = useRef(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState([]);

  useEffect(() => {
    if (!searchOpen && name) {
      setSearchQuery(name);
    }
  }, [name, searchOpen]);

  const handleGeneratePrompt = () => {
    setAiPrompt(buildProductThumbPrompt({ name, details, itemKind }));
  };

  const handleCopyPrompt = async () => {
    const text = aiPrompt || buildProductThumbPrompt({ name, details, itemKind });
    if (!aiPrompt) setAiPrompt(text);
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const url = await uploadProductThumbnail(file);
      onChange?.(url);
    } catch (err) {
      setError(err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const runSearch = useCallback(async () => {
    const q = searchQuery.trim() || name.trim();
    if (!q) {
      setError('Enter a search term');
      return;
    }
    setSearching(true);
    setError('');
    try {
      const token = getStoredToken();
      const res = await axios.get(`${getApiBaseUrl()}/business-posts/stock-images/search`, {
        params: { q, page: 1, pageSize: 20 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const images = res?.data?.data?.images || [];
      setResults(images);
      if (!images.length) setError('No images found — try another search');
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Image search failed');
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [searchQuery, name]);

  const pickStockImage = async (img) => {
    const rawUrl = img?.url || img?.thumbnailUrl || img?.thumbnail || '';
    if (!rawUrl) return;
    setImporting(true);
    setError('');
    try {
      const token = getStoredToken();
      // Prefer server-side import (Cloudinary) when available
      try {
        const res = await axios.get(`${getApiBaseUrl()}/business-posts/stock-images/search`, {
          params: { importUrl: rawUrl },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const uploaded = res?.data?.data;
        const url =
          uploaded?.secure_url ||
          uploaded?.url ||
          uploaded?.imageUrl ||
          '';
        if (url) {
          onChange?.(toOptimizedThumbDeliveryUrl(url, THUMB_PX) || url);
          setSearchOpen(false);
          return;
        }
      } catch {
        /* fall through — use remote URL directly */
      }
      onChange?.(rawUrl);
      setSearchOpen(false);
    } catch (err) {
      setError(err.message || 'Could not use that image');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="product-image-picker" style={{ marginTop: 8 }}>
      <label style={{ fontWeight: 600, display: 'block', marginBottom: 8 }}>
        Product / service image
      </label>

      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setSearchOpen(true);
            setSearchQuery(name || searchQuery);
            setError('');
          }}
        >
          1) Online search
        </button>
        <button type="button" className="btn btn-secondary" onClick={handleGeneratePrompt}>
          2) AI prompt
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Uploading…' : value ? 'Replace upload' : 'Upload image'}
        </button>
        {value ? (
          <button type="button" className="btn btn-link" onClick={() => onChange?.('')}>
            Remove
          </button>
        ) : null}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={handleFile}
      />

      <div
        style={{
          padding: 12,
          borderRadius: 10,
          border: '1px dashed #c4b5fd',
          background: '#f5f3ff',
          marginBottom: 10,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 4, fontSize: 13 }}>
          AI thumbnail prompt · {THUMB_PX}×{THUMB_PX} PNG
        </div>
        <p style={{ margin: '0 0 8px', fontSize: 12, color: '#475569' }}>
          Generate → copy → paste in Gemini / ChatGPT → download → Upload image here.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <button type="button" className="btn btn-primary" onClick={handleGeneratePrompt}>
            Auto generate prompt
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleCopyPrompt}>
            Copy
          </button>
          <a
            className="btn btn-link"
            href="https://gemini.google.com/app"
            target="_blank"
            rel="noreferrer"
          >
            Gemini
          </a>
          <a className="btn btn-link" href="https://chatgpt.com/" target="_blank" rel="noreferrer">
            ChatGPT
          </a>
        </div>
        {aiPrompt ? (
          <textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={4}
            style={{
              width: '100%',
              fontSize: 12,
              padding: 8,
              borderRadius: 6,
              border: '1px solid #ddd',
              background: '#fff',
            }}
          />
        ) : null}
      </div>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div
          style={{
            width: 88,
            height: 88,
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            background: '#f8fafc',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {value ? (
            <img
              src={value}
              alt="Preview"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          ) : (
            <span style={{ fontSize: 12, color: '#94a3b8' }}>No image</span>
          )}
        </div>
        <div style={{ flex: 1 }}>
          <input
            type="url"
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder="Or paste image URL…"
            style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 6 }}
          />
          <small style={{ color: '#64748b' }}>
            Large AI dumps OK · auto-resized to {THUMB_PX}×{THUMB_PX} on upload
          </small>
        </div>
      </div>

      {error ? (
        <div style={{ color: '#b91c1c', fontSize: 13, marginTop: 8 }}>{error}</div>
      ) : null}

      {searchOpen ? (
        <div
          className="modal-overlay"
          style={{ zIndex: 1200 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setSearchOpen(false);
          }}
        >
          <div
            className="modal-content"
            style={{ maxWidth: 640, maxHeight: '85vh', overflow: 'auto' }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Online image search</h2>
              <button type="button" className="modal-close" onClick={() => setSearchOpen(false)}>
                ×
              </button>
            </div>
            <div style={{ padding: '0 4px 16px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), runSearch())}
                  placeholder="e.g. santoor soap, color print…"
                  style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid #ddd' }}
                />
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={searching || importing}
                  onClick={runSearch}
                >
                  {searching ? 'Searching…' : 'Search'}
                </button>
              </div>
              {importing ? <p style={{ fontSize: 13 }}>Importing image…</p> : null}
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                  gap: 8,
                }}
              >
                {results.map((img, idx) => {
                  const thumb = img.thumbnailUrl || img.thumbnail || img.url;
                  return (
                    <button
                      key={`${img.url}-${idx}`}
                      type="button"
                      disabled={importing}
                      onClick={() => pickStockImage(img)}
                      style={{
                        border: '1px solid #e2e8f0',
                        borderRadius: 8,
                        padding: 0,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: '#fff',
                        aspectRatio: '1',
                      }}
                      title={img.attribution || img.source || 'Select'}
                    >
                      <img
                        src={thumb}
                        alt=""
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </button>
                  );
                })}
              </div>
              {!results.length && !searching ? (
                <p style={{ color: '#64748b', fontSize: 13, marginTop: 12 }}>
                  Search free stock photos (Pexels / Openverse), then tap to use.
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ProductImagePicker;
